/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TrainingAllowanceData } from 'src/modules/modelTraining/types/trainingTypes';

const messages = defineMessages({
    emptyProject: {
        id: 'clu.trainingValidation.emptyProject',
        defaultMessage: 'There is no data in your project.'
    },
    projectLoading: {
        id: 'clu.trainingValidation.projectLoading',
        defaultMessage: 'Project is still loading.'
    },
    doesNotHaveValidNumOfEntities: {
        id: 'clu.trainingValidation.doesNotHaveValidNumOfEntities',
        defaultMessage: 'You have {entitiesCount} entities in your project. Maximum allowed is {threshold} entities.'
    },
    belowValidNumOfIntents: {
        id: 'clu.trainingValidation.belowValidNumOfIntents',
        defaultMessage: 'You have {intentsCount} intents in your project. Minimum allowed is {threshold} intent(s).'
    },
    aboveValidNumOfIntents: {
        id: 'clu.trainingValidation.aboveValidNumOfIntents',
        defaultMessage: 'You have {intentsCount} intents in your project. Maximum allowed is {threshold} intent(s).'
    },
    belowValidNumOfUtterances: {
        id: 'clu.trainingValidation.belowValidNumOfUtterances',
        defaultMessage:
            'You have {utterancesCount} labeled utterances in your project. Minimum allowed is {threshold} labeled utterance(s).'
    },
    aboveValidNumOfUtterances: {
        id: 'clu.trainingValidation.aboveValidNumOfUtterances',
        defaultMessage:
            'You have {utterancesCount} labeled utterances in your project. Maximum allowed is {threshold} labeled utterance(s).'
    },
    invalidNumOfSynonyms: {
        id: 'clu.trainingValidation.invalidNumOfSynonyms',
        defaultMessage:
            'You have {synonymsCount} synonyms for the list component in the entity "{entityName}". Maximum allowed is {threshold} synonyms per list component.'
    },
    invalidNumOfPrebuilts: {
        id: 'clu.trainingValidation.invalidNumOfPrebuilts',
        defaultMessage:
            'You have {prebuiltCount} prebuilt components in the entity "{entityName}". Maximum allowed is {threshold} prebuilt components.'
    },
    invalidOrchestrationTags: {
        id: 'clu.trainingValidation.invalidOrchestrationTags',
        defaultMessage: 'You have no labeled utterances or linked projects.'
    }
});

export const getTrainingValidation = (
    currentProject: CluProjectStore,
    localize: (
        messageDescriptor: ReactIntl.FormattedMessage.MessageDescriptor,
        values?: {
            [key: string]: ReactIntl.MessageValue;
        }
    ) => string
): TrainingAllowanceData => {
    const isConversationProject = currentProject.appType === 'Conversation';
    const isOrchestrationProject = currentProject.appType === 'Orchestration';

    const isEmptyProject =
        currentProject.jsonData.utterances.length === 0 &&
        currentProject.jsonData.intents.length === 0 &&
        currentProject.jsonData.entities.length === 0;

    if (isEmptyProject) {
        return { isAllowed: false, message: localize(messages.emptyProject) };
    }

    const isLoading = currentProject.trainingJobStoreHouse.tracker.isRunning;

    if (isLoading) {
        return { isAllowed: false, message: localize(messages.projectLoading) };
    }

    if (isConversationProject) {
        /**
         * Entities count validation
         */
        const entitiesCount = currentProject.entityStoreHouse.items.length;
        const hasValidNumberOfEntities =
            entitiesCount >= LuisConstants.Clu.MinEntitiesCount && entitiesCount <= LuisConstants.Clu.MaxEntitiesCount;

        if (!hasValidNumberOfEntities) {
            return {
                isAllowed: false,
                message: localize(messages.doesNotHaveValidNumOfEntities, { entitiesCount, threshold: LuisConstants.Clu.MaxEntitiesCount })
            };
        }

        /**
         * List entities' synonyms validation
         */
        for (const entityStore of currentProject.entityStoreHouse.items) {
            for (const sublist of entityStore.sublists) {
                if (sublist.synonyms.length > LuisConstants.Clu.MaxCountOfSynonymsPerListEntity) {
                    return {
                        isAllowed: false,
                        message: localize(messages.invalidNumOfSynonyms, {
                            entityName: entityStore.name,
                            synonymsCount: sublist.synonyms.length,
                            threshold: LuisConstants.Clu.MaxCountOfSynonymsPerListEntity
                        })
                    };
                }
            }
        }

        /**
         * Prebuilt entities' validation
         */
        for (const entityStore of currentProject.entityStoreHouse.items) {
            if (entityStore.prebuilts.length > LuisConstants.Clu.MaxNumOfPrebuiltEntitiesPerCompositeEntity) {
                return {
                    isAllowed: false,
                    message: localize(messages.invalidNumOfPrebuilts, {
                        entityName: entityStore.name,
                        prebuiltCount: entityStore.prebuilts.length,
                        threshold: LuisConstants.Clu.MaxNumOfPrebuiltEntitiesPerCompositeEntity
                    })
                };
            }
        }
    }

    /**
     * Intents count validation
     */
    const intentsCount = currentProject.intentStoreHouse.items.length;
    const hasValidNumberOfIntents = intentsCount >= LuisConstants.Clu.MinIntentsCount && intentsCount <= LuisConstants.Clu.MaxIntentsCount;

    if (!hasValidNumberOfIntents) {
        const isBelowThreshold = intentsCount < LuisConstants.Clu.MinIntentsCount;

        const threshold = isBelowThreshold ? LuisConstants.Clu.MinIntentsCount : LuisConstants.Clu.MaxIntentsCount;
        const message = localize(isBelowThreshold ? messages.belowValidNumOfIntents : messages.aboveValidNumOfIntents, {
            intentsCount,
            threshold
        });

        return { isAllowed: false, message };
    }

    /**
     * Tagged utterances count validation
     */
    const utterancesCount = currentProject.jsonData.utterances.filter(utterance => utterance.entities.length !== 0).length;
    const hasValidNumberOfUtterances =
        utterancesCount >= LuisConstants.Clu.MinExamplesCount && utterancesCount <= LuisConstants.Clu.MaxExamplesCount;

    const isAboveThreshold = utterancesCount > LuisConstants.Clu.MaxExamplesCount;

    if ((isConversationProject && !hasValidNumberOfUtterances) || (isOrchestrationProject && isAboveThreshold)) {
        const threshold = isAboveThreshold ? LuisConstants.Clu.MaxExamplesCount : LuisConstants.Clu.MinExamplesCount;
        const message = localize(isAboveThreshold ? messages.aboveValidNumOfUtterances : messages.belowValidNumOfUtterances, {
            utterancesCount,
            threshold
        });

        return { isAllowed: false, message };
    }

    if (isOrchestrationProject) {
        const isLinkedToProj = currentProject.intentStoreHouse.items.filter(intent => Boolean(intent.orchestrationInfo));
        if (!hasValidNumberOfUtterances && !isLinkedToProj) {
            return { isAllowed: false, message: localize(messages.invalidOrchestrationTags) };
        }
    }

    return { isAllowed: true };
};
